import { useCallback, useEffect, useState } from 'react';
import useNavigateWithSearch from '../../../../../../hooks/useNavigateWithSearch';

import GiftCardItem, { GIFT_CARD_ITEM_TYPE } from '../../../GiftCards/GiftCardItem';
import { getGiftCardsBrandsOffer, getGiftCardsBrands } from '../../../../utils/service';
import Slider, { SLIDER_TYPES } from '../../../../global/Slider';
import { FLAGS, getCategoryParam } from '../../../Rewards/utils';
import { BRAND_ID_ROUTE_PARAM, ROUTE_GIFT_CARDS_BRAND_ID } from '../../../../config/routes';
import useHasAccessToGiftCards from '../../../../../../hooks/useHasAccessToGiftCards';
import { getTrackerId, POSTHOG_CAPTURE_ATTRIBUTES, TRACKER_IDS } from '../../../../config/tracker';

import './GiftCardsSlider.scss';

const {
  tbb: {
    lastStep: {
      giftCardsCta: trackerGiftCardsCta,
    },
  },
} = TRACKER_IDS;

export const GIFT_CARDS_SLIDER_TYPES = {
  DEFAULT: 'default',
  OFFER: 'offer',
};

const GiftCardsSlider = ({
  type = GIFT_CARDS_SLIDER_TYPES.DEFAULT,
  disableOnClick = false,
  title,
}) => {
  const navigateWithSearch = useNavigateWithSearch();
  const {
    isLoading: isLoadingHasAccessToGiftCards,
    hasAccessToGiftCards,
  } = useHasAccessToGiftCards();

  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);

    const getGiftCards = type === GIFT_CARDS_SLIDER_TYPES.OFFER
      ? getGiftCardsBrandsOffer
      : getGiftCardsBrands;

    const {
      data: giftCardsData,
      error: giftCardsError,
    } = await getGiftCards({
      pageSize: 6,
      page: 1,
      ...getCategoryParam(FLAGS.IS_HOT),
    });

    if (giftCardsError) {
      setGiftCards([]);
      setLoading(false);
      return;
    }

    setGiftCards(giftCardsData.brands);
    setLoading(false);
  }, []);

  const handleOnClick = useCallback(({ uid }) => {
    const relativePath = ROUTE_GIFT_CARDS_BRAND_ID.replace(BRAND_ID_ROUTE_PARAM, uid);

    navigateWithSearch(relativePath);
  }, [navigateWithSearch]);

  useEffect(() => {
    if (
      type === GIFT_CARDS_SLIDER_TYPES.OFFER ||
      (
        type === GIFT_CARDS_SLIDER_TYPES.DEFAULT &&
        !isLoadingHasAccessToGiftCards &&
        hasAccessToGiftCards
      )
    ) {
      fetchData();
    }
  }, [type, isLoadingHasAccessToGiftCards, hasAccessToGiftCards]);

  if (loading || giftCards.length === 0) {
    return null;
  }

  return (
    <>
      {title}
      <div className="GiftCardsSlider">
        <Slider
          type={SLIDER_TYPES.OVERFLOW}
          autoplay={false}
          rewind={false}
          paginationDots={false}
          gap={8}
          fixedPerPage
          trackPadding={false}
          slidesPerPage={2}
          slides={giftCards.map(giftCardItem => ({
            key: `giftCard-preview-${giftCardItem.uid}`,
            slide: (
              <GiftCardItem
                key={`giftCard-slide-${giftCardItem.uid}`}
                type={GIFT_CARD_ITEM_TYPE.EXTENDED}
                giftCardItem={giftCardItem}
                onClick={disableOnClick ? undefined : handleOnClick}
                trackerProps={{
                  [POSTHOG_CAPTURE_ATTRIBUTES.TRACKER_ID]: getTrackerId(trackerGiftCardsCta),
                }}
              />
            ),
          }))}
        />
      </div>
    </>
  );
};

export default GiftCardsSlider;
